/**
 * Launch settings (policy v2): what a session Orgus launches gets, resolved layer by layer
 * (session over project over org over the built-in defaults) with where every value came
 * from, and the Claude Code settings generated from it: permission rules from the Orgus
 * rules, the model and allowed models, the permission mode. Pure, browser-safe.
 */
import type {
  ApprovalAutonomy,
  EffectiveLaunch,
  EffectiveSetting,
  GeneratedPermission,
  Id,
  PolicyConfig,
  PolicyRule,
  PolicyScope,
  PolicySettingKey,
  PolicySettings,
  SettingSource,
} from '@orgus/protocol';
import { DEFAULT_IDLE_CLOSE_MINUTES, IDLE_CLOSE_MAX_MINUTES } from '@orgus/protocol';
import { matchGlob } from './glob.ts';

/** The built-in layer: what applies when no org or project setting says otherwise. */
export const DEFAULT_SETTINGS: Readonly<Required<Omit<PolicySettings, 'defaultModel' | 'allowedModels' | 'allowedAgentKinds' | 'prReview' | 'idleCloseMinutes'>>> = {
  permissionMode: 'default',
  disableBypassPermissions: false,
  autonomy: 'ask',
  secrets: [],
  env: {},
  requiredChecks: ['tests'],
  reviewer: 'none',
};

export const SETTING_LABEL: Record<PolicySettingKey | 'model', string> = {
  model: 'Model',
  defaultModel: 'Default model',
  allowedModels: 'Allowed models',
  permissionMode: 'Permission mode',
  disableBypassPermissions: 'Block bypass permissions',
  autonomy: 'Autonomy',
  secrets: 'Secrets leased',
  env: 'Environment',
  requiredChecks: 'Required checks',
  reviewer: 'Reviewer before landing',
  allowedAgentKinds: 'Allowed agent kinds',
  prReview: 'Pull request reviews',
  idleCloseMinutes: 'Close idle sessions after',
};

export const PERMISSION_MODES = ['default', 'acceptEdits', 'plan', 'dontAsk', 'bypassPermissions'] as const;

export function emptyConfig(): PolicyConfig {
  return { org: {}, projects: {}, freezes: [] };
}

/**
 * R-FN-6: how long a session Orgus launched in this project may sit idle before Orgus closes
 * it, in ms: the project's idleCloseMinutes, over the org's, over DEFAULT_IDLE_CLOSE_MINUTES. 0: never.
 */
export function idleCloseMsFor(config: PolicyConfig | undefined, projectId: Id | null): number {
  const project = projectId ? config?.projects[projectId]?.idleCloseMinutes : undefined;
  const minutes = project ?? config?.org.idleCloseMinutes ?? DEFAULT_IDLE_CLOSE_MINUTES;
  return Number.isFinite(minutes) && minutes > 0 ? Math.min(minutes, IDLE_CLOSE_MAX_MINUTES) * 60_000 : 0;
}

/** ADR 0070: the project's autonomy, over the org's, over the default (ask). */
export function autonomyFor(config: PolicyConfig | undefined, projectId: Id | null): ApprovalAutonomy {
  const project = projectId ? config?.projects[projectId]?.autonomy : undefined;
  return project ?? config?.org.autonomy ?? 'ask';
}

/** Is this model allowed by the list (globs, case-insensitive)? No list: any model. */
export function modelAllowed(model: string, allowed: readonly string[] | undefined): boolean {
  if (!allowed) return true;
  const m = model.trim();
  return allowed.some((g) => matchGlob(g.trim(), m, true) || matchGlob(`*${g.trim()}*`, m, true));
}

const hasGlob = (s: string) => /[*?]/.test(s);

interface Layer {
  source: SettingSource;
  values: PolicySettings;
}

function layersFor(config: PolicyConfig, projectId: Id | null): Layer[] {
  const out: Layer[] = [
    { source: 'default', values: DEFAULT_SETTINGS as PolicySettings },
    { source: 'org', values: config.org ?? {} },
  ];
  const p = projectId ? config.projects?.[projectId] : undefined;
  if (p) out.push({ source: 'project', values: p });
  return out;
}

/** One key resolved through the layers: the highest layer that sets it wins. */
function resolveKey(layers: Layer[], key: PolicySettingKey): EffectiveSetting {
  let win: Layer | null = null;
  const overridden: EffectiveSetting['overridden'] = [];
  for (const l of layers) {
    const v = l.values[key];
    if (v === undefined) continue;
    if (win) overridden.unshift({ source: win.source, value: win.values[key] });
    win = l;
  }
  return { key, label: SETTING_LABEL[key], value: win ? win.values[key] : undefined, source: win?.source ?? 'default', overridden };
}

/** Environment variables are resolved per name: a project value replaces the org value for that name only. */
function resolveEnv(layers: Layer[]): EffectiveSetting[] {
  const names = new Set<string>();
  for (const l of layers) for (const k of Object.keys(l.values.env ?? {})) names.add(k);
  return [...names].sort().map((name) => {
    let win: Layer | null = null;
    const overridden: EffectiveSetting['overridden'] = [];
    for (const l of layers) {
      const v = l.values.env?.[name];
      if (v === undefined) continue;
      if (win) overridden.unshift({ source: win.source, value: win.values.env?.[name] });
      win = l;
    }
    return { key: 'env', label: name, value: win?.values.env?.[name], source: win?.source ?? 'default', overridden };
  });
}

/** Every setting for a project (and optionally a session), with where it came from. */
export function resolveSettings(config: PolicyConfig, projectId: Id | null): EffectiveSetting[] {
  const layers = layersFor(config, projectId);
  const keys: PolicySettingKey[] = ['defaultModel', 'allowedModels', 'permissionMode', 'disableBypassPermissions', 'autonomy', 'secrets', 'requiredChecks', 'reviewer', 'prReview'];
  return [...keys.map((k) => resolveKey(layers, k)), ...resolveEnv(layers)];
}

const CANONICAL_TOOL: Record<string, string> = {
  bash: 'Bash',
  powershell: 'PowerShell',
  edit: 'Edit',
  write: 'Write',
  multiedit: 'MultiEdit',
  notebookedit: 'NotebookEdit',
  read: 'Read',
  glob: 'Glob',
  grep: 'Grep',
  webfetch: 'WebFetch',
  websearch: 'WebSearch',
  agent: 'Agent',
  task: 'Agent',
  skill: 'Skill',
};
const PATH_TOOLS = new Set(['Edit', 'Write', 'MultiEdit', 'NotebookEdit', 'Read']);
const SHELL = new Set(['Bash', 'PowerShell']);

/** A path pattern in Claude Code's syntax: relative stays relative, '/abs' becomes '//abs', one '*' becomes '**'. */
function pathSpecifier(pattern: string): string | null {
  const p = pattern.replace(/\\/g, '/');
  if (/^[a-zA-Z]:/.test(p) || p.startsWith('//')) return null;
  const globbed = p.replace(/(?<!\*)\*(?!\*)/g, '**');
  return p.startsWith('/') ? `/${globbed}` : globbed;
}

/** One Orgus rule as a Claude Code permission rule, or why it cannot be one. */
export function toClaudeRule(rule: PolicyRule): { rule: string } | { why: string } {
  const m = rule.match ?? {};
  if (m.action) return { why: 'Orgus action rules apply in the hub, not in Claude Code.' };
  if (m.model) return { why: 'Rules for one model are applied by the hook.' };
  const raw = (m.tool ?? '').trim();
  if (!raw) return { why: 'A rule without a tool is applied by the hook.' };
  let tool = CANONICAL_TOOL[raw.toLowerCase()] ?? raw;
  const mcp = /^mcp__[A-Za-z0-9_-]+?(?:__\*|\*)?$/.test(raw);
  if (mcp) tool = raw.replace(/(?:__)?\*$/, '');
  else if (hasGlob(raw)) return { why: 'Tool globs are applied by the hook.' };
  if (!/^[A-Za-z][A-Za-z0-9_-]*$/.test(tool)) return { why: 'That tool name cannot be written as a Claude Code rule.' };
  const pattern = (m.pattern ?? '').trim();
  if (!pattern) return { rule: tool };
  if (/[()]/.test(pattern)) return { why: 'Patterns with brackets are applied by the hook.' };
  if (SHELL.has(tool)) return { rule: `${tool}(${pattern})` };
  if (PATH_TOOLS.has(tool)) {
    const spec = pathSpecifier(pattern);
    return spec ? { rule: `${tool}(${spec})` } : { why: 'Drive-letter paths are applied by the hook.' };
  }
  return { why: `Patterns for ${tool} are applied by the hook.` };
}

/**
 * Claude Code permission rules for a launch in this project. Orgus lets project rules win
 * over org rules; Claude Code lets deny win over allow. So when a project has rules for a
 * tool, the org rules for that tool stay with the hook (which applies both in order) and
 * the generated rules never disagree with what the hook decides.
 */
export function claudePermissions(rules: readonly PolicyRule[], projectId: Id | null): { permissions: GeneratedPermission[]; hookOnly: { ruleId: Id; why: string }[] } {
  const live = rules.filter((r) => r.enabled !== false && (r.scope.kind === 'org' || (projectId && r.scope.projectId === projectId)));
  const projectTools = new Set(live.filter((r) => r.scope.kind === 'project' && r.match.tool).map((r) => (r.match.tool ?? '').toLowerCase()));
  const permissions: GeneratedPermission[] = [];
  const hookOnly: { ruleId: Id; why: string }[] = [];
  const seen = new Set<string>();
  for (const r of live) {
    if (r.scope.kind === 'org' && r.match.tool && projectTools.has(r.match.tool.toLowerCase())) {
      hookOnly.push({ ruleId: r.id, why: 'A project rule for this tool takes precedence; the hook applies both in order.' });
      continue;
    }
    const c = toClaudeRule(r);
    if ('why' in c) {
      hookOnly.push({ ruleId: r.id, why: c.why });
      continue;
    }
    const key = `${r.decision}:${c.rule}`;
    if (seen.has(key)) continue;
    seen.add(key);
    permissions.push({ rule: c.rule, list: r.decision, source: r.scope.kind, ruleId: r.id });
  }
  return { permissions, hookOnly };
}

export interface SessionChoice {
  model?: string;
  permissionMode?: string;
}

/**
 * What a session launched in this project gets: settings with their source, the model it
 * runs, generated permission rules, the Claude Code settings object and any violation that
 * must refuse the launch.
 */
export function effectiveLaunch(rules: readonly PolicyRule[], config: PolicyConfig, projectId: Id | null, session: SessionChoice = {}): EffectiveLaunch {
  const settings = resolveSettings(config, projectId);
  const get = (k: Exclude<PolicySettingKey, 'env'>) => settings.find((s) => s.key === k);
  const violations: string[] = [];
  const allowed = get('allowedModels')?.value as string[] | undefined;
  const defaultModel = get('defaultModel');

  // Model: the session's choice, else the default model, else Claude Code's own default.
  const overridden: EffectiveSetting['overridden'] = [];
  let model: string | null = null;
  let modelSource: SettingSource = 'default';
  let note: string | undefined;
  if (defaultModel?.value) {
    model = String(defaultModel.value);
    modelSource = defaultModel.source;
  }
  const chosen = session.model?.trim();
  if (chosen) {
    if (model) overridden.push({ source: modelSource, value: model });
    model = chosen;
    modelSource = 'session';
  }
  if (model && !modelAllowed(model, allowed)) {
    violations.push(`The model ${model} is not allowed here. Allowed: ${(allowed ?? []).join(', ') || 'none'}. Pick one of those, or ask an admin to change the model policy.`);
  }
  if (!model && allowed && allowed.length) {
    const first = allowed.find((a) => !hasGlob(a));
    if (first) {
      model = first;
      modelSource = get('allowedModels')!.source;
      note = 'No default model is set, so the first allowed model is used.';
    } else {
      note = 'Claude Code picks its default model; the hub records it and flags it if it is not allowed.';
    }
  }
  const modelSetting: EffectiveSetting = { key: 'model', label: SETTING_LABEL.model, value: model, source: modelSource, overridden, ...(note ? { note } : {}) };

  // Permission mode: the session's choice over the layers.
  const modeSetting = { ...get('permissionMode')! };
  const chosenMode = session.permissionMode?.trim();
  if (chosenMode && chosenMode !== modeSetting.value) {
    modeSetting.overridden = [{ source: modeSetting.source, value: modeSetting.value }, ...modeSetting.overridden];
    modeSetting.value = chosenMode;
    modeSetting.source = 'session';
  }
  const mode = String(modeSetting.value ?? 'default');
  const noBypass = get('disableBypassPermissions')?.value === true;
  if (noBypass && mode === 'bypassPermissions') violations.push('Bypass permissions is turned off for this project. Start the session in another permission mode.');

  const { permissions, hookOnly } = claudePermissions(rules, projectId);
  const list = (l: GeneratedPermission['list']) => permissions.filter((p) => p.list === l).map((p) => p.rule);
  const perms: Record<string, unknown> = { allow: list('allow'), ask: list('ask'), deny: list('deny') };
  if (modeSetting.source !== 'default') perms.defaultMode = mode;
  if (noBypass) perms.disableBypassPermissionsMode = 'disable';
  const claude: Record<string, unknown> = { permissions: perms };
  if (model) claude.model = model;
  if (allowed && allowed.length && !allowed.some(hasGlob)) claude.availableModels = [...allowed];
  const envEntries = settings.filter((s) => s.key === 'env');
  if (envEntries.length) claude.env = Object.fromEntries(envEntries.map((e) => [e.label, String(e.value)]));

  const secrets = ((get('secrets')?.value as string[] | undefined) ?? []).filter(Boolean);
  return {
    projectId,
    model,
    settings: sortSettings([modelSetting, ...settings.filter((s) => s.key !== 'permissionMode'), modeSetting]),
    permissions,
    hookOnly,
    claude,
    secrets,
    violations,
  };
}

/** A settings layer that, as saved, refuses launches: per project (null: no project), why. */
export interface LayerConflict {
  projectId: Id | null;
  violations: string[];
}

/**
 * Launches a settings layer would refuse with no choice from the person starting them: a
 * default model outside the allowed models, or a bypass permission mode where bypass is
 * turned off. An org layer is checked for sessions outside any project and for every project
 * with its own layer; a project layer for that project.
 */
export function layerConflicts(rules: readonly PolicyRule[], config: PolicyConfig, scope: PolicyScope): LayerConflict[] {
  const ids: (Id | null)[] = scope.kind === 'project' ? [scope.projectId] : [null, ...Object.keys(config.projects)];
  const out: LayerConflict[] = [];
  for (const projectId of ids) {
    const { violations } = effectiveLaunch(rules, config, projectId);
    if (violations.length) out.push({ projectId, violations });
  }
  return out;
}

const ORDER: (PolicySettingKey | 'model')[] = ['model', 'defaultModel', 'allowedModels', 'permissionMode', 'disableBypassPermissions', 'autonomy', 'secrets', 'env', 'requiredChecks', 'reviewer'];
function sortSettings(list: EffectiveSetting[]): EffectiveSetting[] {
  return [...list].sort((a, b) => ORDER.indexOf(a.key) - ORDER.indexOf(b.key) || a.label.localeCompare(b.label));
}
